// src/pages/public/StateLandingPage.tsx
import { useMemo } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import PublicSeoPage from './PublicSeoPage';
import { useSeoInventory, usePublicDirectory } from './usePublicSeoData';
import { citiesInState, statesWithInventory } from './publicPageHelpers';
import { getSeoContext } from '../../seo/seoContext';
import { buildStateModel } from '../../seo/core/pageModels';
import { resolveState } from '../../seo/core/states';
import { analyzeFacetedUrl, applyFacetsToMetadata } from '../../seo/core/facets';
import { ROUTES } from '../../seo/core/urls';
import type { PublicPageModel } from '../../seo/types';

/**
 * `/locations/:stateSlug` — state hub, defined for all 50 states + DC.
 *
 * A state with no live professionals still resolves and still renders something
 * useful ("be the first professional in X"), but it is noindex and excluded
 * from the sitemap until it clears the eligibility bar.
 *
 * Non-canonical spellings (`/locations/TX`, `/locations/Texas`) 301-equivalent
 * redirect to `/locations/texas` so one market never has two URLs.
 */
function StateLandingPage() {
  const ctx = getSeoContext();
  const { stateSlug } = useParams<{ stateSlug: string }>();
  const [searchParams] = useSearchParams();
  const state = resolveState(stateSlug);
  const { inventory, loading: inventoryLoading } = useSeoInventory();

  const { directory, loading: directoryLoading } = usePublicDirectory(
    { state: state?.code, limit: 24 },
    Boolean(state)
  );

  const model = useMemo<PublicPageModel | null>(() => {
    if (!state) return null;
    const cities = citiesInState(inventory, state.code);
    const stylistCount = cities.reduce((sum, row) => sum + (Number(row.stylistCount) || 0), 0);

    const built = buildStateModel(
      {
        state: state.code,
        stylistCount: stylistCount || directory.total,
        cities: cities
          .map((row) => ({ city: row.city, state: row.state, stylistCount: row.stylistCount }))
          .sort((a, b) => b.stylistCount - a.stylistCount),
        stylists: directory.stylists,
        siblingStates: statesWithInventory(inventory).slice(0, 12),
      },
      ctx
    ) as PublicPageModel | null;
    if (!built) return null;

    const analysis = analyzeFacetedUrl(ROUTES.state(state.slug), searchParams, {
      baseIndexable: built.metadata.indexable,
    });
    return { ...built, metadata: applyFacetsToMetadata(built.metadata, analysis, ctx) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, inventory, directory, searchParams.toString(), ctx.environment, ctx.siteUrl]);

  if (!state) return <Navigate to={ROUTES.locationIndex()} replace />;
  if (state.slug !== stateSlug) return <Navigate to={ROUTES.state(state.slug)} replace />;

  return <PublicSeoPage model={model} loading={inventoryLoading || directoryLoading} />;
}

export default StateLandingPage;
